import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/mocks/videos";
import { typeLabel } from "@/utils/format";

interface HeroCarouselProps {
  videos: Video[];
}

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = videos.length;

  useEffect(() => {
    if (count <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  const go = (next: number) => {
    setIndex(((next % count) + count) % count);
  };

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden bg-secondary-900">
      {videos.map((video, i) => (
        <div
          key={video.id}
          className={`absolute inset-0 transition-opacity duration-700 ease-out ${
            i === index ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          <img
            src={video.backdrop}
            alt={`${video.title} 焦点图`}
            title={`${video.title} - 在线视频`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>
        </div>
      ))}

      {/* Content */}
      <div className="relative z-20 h-full w-full flex items-center">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-[1200px] mx-auto">
            <div className="max-w-2xl">
              <div className="flex items-center gap-2 mb-4 animate-fade-up">
                <span className="px-2.5 py-1 rounded-full bg-accent-500 text-background-50 dark:text-foreground-950 text-xs font-semibold">
                  {typeLabel(videos[index].type)}
                </span>
                {videos[index].isNew && (
                  <span className="px-2.5 py-1 rounded-full bg-primary-500 text-background-50 dark:text-foreground-950 text-xs font-semibold">
                    最新上线
                  </span>
                )}
                <span className="flex items-center gap-1 text-background-100 text-xs font-medium">
                  <i className="ri-star-fill text-accent-400"></i>
                  {videos[index].rating.toFixed(1)}
                </span>
              </div>

              <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight animate-fade-up">
                {videos[index].title}
              </h2>
              <p className="mt-3 text-base md:text-lg text-background-200 animate-fade-up">
                {videos[index].subtitle}
              </p>
              <p className="mt-4 text-sm md:text-base text-background-300 line-clamp-2 max-w-xl animate-fade-up">
                {videos[index].description}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-300">
                <span>{videos[index].year}</span>
                <span>{videos[index].region}</span>
                <span>{videos[index].duration}</span>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${videos[index].id}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-play-fill text-xl"></i>
                  立即观看
                </Link>
                <Link
                  to={`/category/${videos[index].type}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-50/15 text-white font-semibold whitespace-nowrap border border-background-50/30 backdrop-blur-sm hover:bg-background-50/25 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-xl"></i>
                  更多同类
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Arrows */}
      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一张"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 text-white border border-background-50/30 backdrop-blur-sm hover:bg-background-50/35 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一张"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-30 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 text-white border border-background-50/30 backdrop-blur-sm hover:bg-background-50/35 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
        {videos.map((video, i) => (
          <button
            key={video.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到第 ${i + 1} 张`}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              i === index ? "w-8 bg-primary-500" : "w-3 bg-background-50/50"
            }`}
          ></button>
        ))}
      </div>
    </section>
  );
}